// ============================================================
// HARMONIC CHARTER — App Root
// ============================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { Toolbar } from './components/editor/Toolbar';
import { ChartRenderer } from './components/renderer/ChartRenderer';
import { LibraryModal } from './components/library/LibraryModal';
import { AuthModal } from './components/auth/AuthModal';
import { ShareModal } from './components/auth/ShareModal';
import { PrintSettingsModal } from './components/editor/PrintSettingsModal';
import { PlaybackProvider } from './components/editor/PlaybackContext';
import { DocsPanel } from './components/docs/DocsPanel';
import { PresenceStaleCleanup } from './components/editor/PresenceLayer';
import { useSongStore } from './store/songStore';
import { useLibraryStore } from './store/libraryStore';
import { useAuthStore } from './store/authStore';
import { useCollaborationStore } from './store/collaborationStore';
import { formatKey } from './core/formatter';
import {
  pullSongs,
  pushSong,
  subscribeToChart,
  broadcastChartUpdate,
  broadcastPresence,
  broadcastLeave,
  unsubscribeFromChart,
  getMyCollaborationMode,
} from './core/cloudSync';
import { loadSong, loadIndex } from './core/library';
import { getLastOpenedSongId } from './store/libraryStore';
import type { Song } from './core/types';

const AUTOSAVE_INTERVAL = 30_000;
const PRESENCE_INTERVAL = 8_000;

// ------------------------------------------------------------
// LOCAL-ONLY FIELDS
// These fields are personal viewing preferences and must never
// be broadcast to or overwritten by collaborators in linked mode.
// ------------------------------------------------------------

type LocalOnlyFields = Pick<Song, 'displayMode' | 'analysisSettings'>;

/** Strip local-only fields before sending to collaborators. */
function stripLocalFields(song: Song): Omit<Song, keyof LocalOnlyFields> {
  const { displayMode, analysisSettings, ...rest } = song;
  void displayMode; void analysisSettings;
  return rest;
}

/** Apply an incoming song update while preserving local-only fields. */
function applyRemoteUpdate(incoming: Song, local: Song): Song {
  return {
    ...incoming,
    displayMode:      local.displayMode,
    analysisSettings: local.analysisSettings,
  };
}

export default function App() {
  const song                 = useSongStore((s) => s.song);
  const isDirty              = useSongStore((s) => s.isDirty);
  const loadFromLocalStorage = useSongStore((s) => s.loadFromLocalStorage);
  const saveToLocalStorage   = useSongStore((s) => s.saveToLocalStorage);
  const setSongTitle         = useSongStore((s) => s.setSongTitle);
  const setSongNote          = useSongStore((s) => s.setSongNote);

  const saveCurrentSong      = useLibraryStore((s) => s.saveCurrentSong);
  const setActiveSongId      = useLibraryStore((s) => s.setActiveSongId);
  const syncFoldersFromCloud = useLibraryStore((s) => s.syncFoldersFromCloud);

  const { user, session, loading: authLoading, init: initAuth } = useAuthStore();

  const {
    mode:          collabMode,
    setMode:       setCollabMode,
    upsertPresence,
    removePresence,
    clearPresence,
  } = useCollaborationStore();

  const [showAuth, setShowAuth]   = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [annotationMode, setAnnotationMode] = useState(false);
  const [docsOpen, setDocsOpen]             = useState(false);

  const localBarFocusRef  = useRef<string | null>(null);
  const isRemoteUpdateRef = useRef(false);

  // ── Title editing ─────────────────────────────────────────

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue]     = useState(song.title);
  const titleInputRef                   = useRef<HTMLInputElement>(null);

  useEffect(() => { setTitleValue(song.title); }, [song.title]);
  useEffect(() => {
    if (editingTitle) {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }
  }, [editingTitle]);

  const commitTitle = useCallback(() => {
    const t = titleValue.trim() || 'Untitled';
    setSongTitle(t);
    setEditingTitle(false);
  }, [titleValue, setSongTitle]);

  // ── Song note editing ─────────────────────────────────────

  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue]     = useState(song.note ?? '');
  const noteInputRef                  = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setNoteValue(song.note ?? ''); }, [song.note]);
  useEffect(() => { if (editingNote) noteInputRef.current?.focus(); }, [editingNote]);

  const commitNote = useCallback(() => {
    const t = noteValue.trim();
    setSongNote(t || null);
    setEditingNote(false);
  }, [noteValue, setSongNote]);

  // ── Subscribe helper ──────────────────────────────────────

  const subscribeLinked = useCallback(async (songId: string) => {
    await subscribeToChart(
      songId,
      (incomingSong) => {
        // FIX: preserve the local user's display preferences when
        // applying a remote update — don't let the other user's
        // displayMode or analysisSettings overwrite ours.
        const localSong = useSongStore.getState().song;
        const merged    = applyRemoteUpdate(incomingSong, localSong);
        isRemoteUpdateRef.current = true;
        useSongStore.setState({ song: merged, isDirty: false });
      },
      (presence) => { upsertPresence(presence); },
      (userId)   => { removePresence(userId); },
    );
  }, [upsertPresence, removePresence]);

  // ── Song load handler ─────────────────────────────────────

  const handleSongLoad = useCallback(async (s: Song) => {
    const currentMode = useCollaborationStore.getState().mode;
    const currentUser = useAuthStore.getState().user;

    if (currentMode === 'linked') {
      if (currentUser) await broadcastLeave(s.id, currentUser.id);
      await unsubscribeFromChart();
      clearPresence();
    }

    useSongStore.setState({
      song:      s,
      undoStack: [],
      redoStack: [],
      isDirty:   false,
      selection: null,
    });
    setActiveSongId(s.id);

    if (currentUser) {
      const mode = await getMyCollaborationMode(s.id);
      if (mode === 'linked' || mode === 'owner') {
        setCollabMode('linked', s.id);
        await subscribeLinked(s.id);
      } else if (mode === 'view') {
        setCollabMode('view', s.id);
      } else if (mode === 'write') {
        setCollabMode('write', s.id);
      } else {
        setCollabMode('none', null);
      }
    }
  }, [setCollabMode, setActiveSongId, clearPresence, subscribeLinked]);

  // ── Bar focus: broadcast presence immediately ─────────────

  useEffect(() => {
    if (collabMode !== 'linked' || !user) return;
    const handler = (e: CustomEvent) => {
      const barId = (e.detail as string | null) ?? null;
      localBarFocusRef.current = barId;
      broadcastPresence(song.id, user.id, user.email ?? '', barId);
    };
    window.addEventListener('bar-focus', handler as EventListener);
    return () => window.removeEventListener('bar-focus', handler as EventListener);
  }, [collabMode, user, song.id]);

  // ── Keepalive presence ping every 8s ─────────────────────

  useEffect(() => {
    if (collabMode !== 'linked' || !user) return;
    const timer = setInterval(() => {
      broadcastPresence(song.id, user.id, user.email ?? '', localBarFocusRef.current);
    }, PRESENCE_INTERVAL);
    broadcastPresence(song.id, user.id, user.email ?? '', localBarFocusRef.current);
    return () => clearInterval(timer);
  }, [collabMode, user, song.id]);

  // ── Broadcast chart updates ───────────────────────────────

  const prevSongRef = useRef<Song>(song);
  useEffect(() => {
    if (collabMode !== 'linked' || !user) return;
    if (prevSongRef.current === song) return;
    prevSongRef.current = song;

    if (isRemoteUpdateRef.current) {
      isRemoteUpdateRef.current = false;
      return;
    }

    // FIX: strip local-only fields before broadcasting so the other
    // user's display mode and analysis settings are never overwritten.
    broadcastChartUpdate(song.id, song, stripLocalFields(song) as Song);
  }, [collabMode, user, song]);

  // ── Cleanup on unmount ────────────────────────────────────

  useEffect(() => {
    return () => {
      const currentUser = useAuthStore.getState().user;
      const currentMode = useCollaborationStore.getState().mode;
      const currentSong = useSongStore.getState().song;
      if (currentUser && currentMode === 'linked') {
        broadcastLeave(currentSong.id, currentUser.id);
        unsubscribeFromChart();
      }
    };
  }, []);

  // ── Auth + cloud init ─────────────────────────────────────

  useEffect(() => { initAuth(); }, [initAuth]);

  useEffect(() => {
    if (!user || !session || authLoading) return;
    (async () => {
      await syncFoldersFromCloud();
      await pullSongs();

      const lastId = getLastOpenedSongId();
      const index  = loadIndex();

      if (lastId && index.songs.length > 0) {
        const file = loadSong(lastId);
        if (file?.song) { handleSongLoad(file.song); return; }
      }

      if (index.songs.length > 0) {
        const mostRecent = [...index.songs].sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )[0];
        const file = loadSong(mostRecent.id);
        if (file?.song) handleSongLoad(file.song);
      }
    })();
  }, [user, session, authLoading, syncFoldersFromCloud, handleSongLoad]);

  // ── Autosave ──────────────────────────────────────────────

  useEffect(() => { loadFromLocalStorage(); }, [loadFromLocalStorage]);

  useEffect(() => {
    if (!isDirty) return;
    const timer = setInterval(() => {
      saveToLocalStorage();
      saveCurrentSong(song);
    }, AUTOSAVE_INTERVAL);
    return () => clearInterval(timer);
  }, [isDirty, saveToLocalStorage, saveCurrentSong, song]);

  useEffect(() => {
    const handle = () => { saveToLocalStorage(); saveCurrentSong(song); };
    window.addEventListener('beforeunload', handle);
    return () => window.removeEventListener('beforeunload', handle);
  }, [saveToLocalStorage, saveCurrentSong, song]);

  // ── Document title ────────────────────────────────────────

  useEffect(() => {
    document.title = isDirty
      ? `• ${song.title} — Harmonic Charter`
      : `${song.title} — Harmonic Charter`;
  }, [song.title, isDirty]);

  const handleShowShare = useCallback(async () => {
    const currentUser = useAuthStore.getState().user;
    const currentSong = useSongStore.getState().song;
    if (currentUser) await pushSong(currentSong, currentUser.id);
    setShowShare(true);
  }, []);

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="flex flex-col bg-paper" style={{ height: '100dvh', overflow: 'hidden' }}>

      <PresenceStaleCleanup />

      <Toolbar
        onShowAuth={() => setShowAuth(true)}
        onShowShare={handleShowShare}
        onShowPrint={() => setShowPrint(true)}
        onSongLoad={handleSongLoad}
        annotationMode={annotationMode}
        onToggleAnnotationMode={() => setAnnotationMode((m) => !m)}
        docsOpen={docsOpen}
        onToggleDocs={() => setDocsOpen((v) => !v)}
      />

      <main className="chart-scroll-area flex-1 overflow-y-auto" style={{ position: 'relative', zIndex: 1 }}>
        <div className="mx-auto px-4 sm:px-8 pt-6 sm:pt-10 pb-24" style={{ maxWidth: 'var(--chart-max-width)' }}>

          <div className="song-header mb-6 sm:mb-8">

            {editingTitle ? (
              <input
                ref={titleInputRef} type="text" value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTitle();
                  if (e.key === 'Escape') { setTitleValue(song.title); setEditingTitle(false); }
                }}
                className="song-title-input mb-3 w-full"
                spellCheck={false}
              />
            ) : (
              <h1
                className="song-title mb-3 cursor-text select-none"
                onDoubleClick={() => setEditingTitle(true)}
                title="Double-click to rename"
              >
                {song.title}
              </h1>
            )}

            <div className="song-meta-row metadata-row">
              <span><span className="text-ink-muted">Key </span>{formatKey(song.defaultKey)}</span>
              <span className="text-rule">·</span>
              <span><span className="text-ink-muted">Time </span>{song.timeSignature.numerator}/{song.timeSignature.denominator}</span>
              {song.tempo && <><span className="text-rule">·</span><span>{song.tempo}</span></>}
              {song.feel  && <><span className="text-rule">·</span><span>{song.feel}</span></>}
            </div>

            {annotationMode && (
              <div style={{
                marginTop: 12,
                background: 'rgba(230, 57, 70, 0.08)',
                border: '1px solid rgba(230, 57, 70, 0.3)',
                borderRadius: 6, padding: '6px 12px',
                fontSize: 12, color: '#e63946',
                fontFamily: 'var(--font-mono)',
              }}>
                ✏️ Annotation mode — click anywhere on the chart to place an annotation. Press ✏️ again to exit.
              </div>
            )}

            <div className="mt-3">
              {editingNote ? (
                <textarea
                  ref={noteInputRef} value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value)}
                  onBlur={commitNote}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { setNoteValue(song.note ?? ''); setEditingNote(false); }
                  }}
                  placeholder="Add a note for this chart… (arrangement cues, patches, performance reminders)"
                  rows={2}
                  style={{
                    width: '100%', fontFamily: 'var(--font-mono)', fontSize: 12,
                    color: 'var(--color-ink-secondary)', background: 'var(--color-paper-alt)',
                    border: '1px solid var(--color-accent)', borderRadius: 5,
                    padding: '6px 10px', outline: 'none', resize: 'vertical', lineHeight: 1.5,
                  }}
                />
              ) : song.note ? (
                <p
                  onClick={() => setEditingNote(true)}
                  className="song-note-print"
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 12,
                    color: 'var(--color-ink-secondary)', fontStyle: 'italic',
                    cursor: 'text', lineHeight: 1.5, padding: '2px 0',
                  }}
                  title="Click to edit note"
                >
                  {song.note}
                </p>
              ) : (
                <button
                  onClick={() => setEditingNote(true)}
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11,
                    color: 'var(--color-ink-muted)', background: 'none',
                    border: 'none', cursor: 'pointer', padding: '2px 0', opacity: 0.5,
                  }}
                >
                  + Add chart note
                </button>
              )}
            </div>
          </div>

          <PlaybackProvider>
            <ChartRenderer
              annotationMode={annotationMode}
              onExitAnnotationMode={() => setAnnotationMode(false)}
            />
          </PlaybackProvider>

        </div>
      </main>

      <LibraryModal onSongLoad={handleSongLoad} />
      {showAuth  && <AuthModal  onClose={() => setShowAuth(false)} />}
      {showShare && <ShareModal onClose={() => setShowShare(false)} />}
      {showPrint && <PrintSettingsModal onClose={() => setShowPrint(false)} />}

      <DocsPanel open={docsOpen} onClose={() => setDocsOpen(false)} />

    </div>
  );
}